<template>
  <div class="news-container">
    <img
      class="banner"
      :src="require('@/assets/images/news-banner.png')"
      alt="bg"
    />
    <div class="tabs-container">
      <Tabs
        :tabs="tabItems"
        :activeIndex.sync="currentIndex"
        @update:activeIndex="handleIndexChange"
      />
    </div>
    <div class="tabs-content" ref="tabsContent">
      <aside class="left-side" ref="leftSide" :style="{ top: currentTopPx + 'px' }">
        <I18nImage filename="news-logo.png" />
        <div>
          <I18nImage filename="news-qrcode.png" />
        </div>
      </aside>
      <main class="right-side">
        <transition name="fade" mode="out-in">
          <div v-if="loading" key="loading" class="tab-loading">加载中...</div>
          <div class="news-list">
            <div v-if="!loading && newsList.length === 0" class="news-empty">
              暂无新闻
            </div>
            <div
              class="news-item"
              v-for="item in newsList"
              :key="item.id"
              @click="handleNavigate(item)"
            >
              <div class="news-item-cover-wrapper">
                <img
                  class="news-item-cover"
                  :src="item.cover_img"
                  :alt="item.title"
                />
              </div>
              <span class="news-item-title">{{ item.title }}</span>
              <div class="news-item-info">
                <span class="news-item-day">
                  <span style="font-size: 20px">
                    {{ formatDate(item.create_time).split(' / ')[0] }}
                  </span>
                  <span style="font-size: 16px">
                    / {{ formatDate(item.create_time).split(' / ')[1] }} /
                    {{ formatDate(item.create_time).split(' / ')[2] }}
                  </span>
                </span>
                <div class="news-item-read">
                  {{ $t('news.p4') }}：{{ item.pv }}
                </div>
              </div>
              <div class="news-item-desc">{{ item.desc }}</div>
            </div>
            <el-pagination
              v-if="newsList.length > 0"
              style="text-align: center; margin: 60px auto; margin-bottom: 50px"
              background
              layout="prev, pager, next"
              :page-size="pageSize"
              :total="total"
              :current-page.sync="currentPage"
              @current-change="handlePageChange"
            ></el-pagination>
          </div>
        </transition>
      </main>
    </div>
  </div>
</template>

<script>
import Tabs from '@/components/Tabs.vue';
import I18nImage from '@/components/I18nImage.vue';
import { getLang } from '@/locale/utils';
import { getPageId } from '@/js/nowRoute';
import dayjs from 'dayjs';

const DEFAULT_PID = 14;

const NewsType = {
  Link: 2,
  Content: 1,
};

export default {
  name: 'About',
  components: {
    Tabs,
    I18nImage,
  },
  data() {
    return {
      tabItems: [],
      newsList: [],
      currentPage: 1,
      total: 0,
      pageSize: 10,
      currentIndex: 0,
      content: '',
      loading: false,
      currentTopPx: 0, // Initial top value, same as original CSS
    };
  },
  mounted: async function () {
    await this.fetchTabs();

    if (this.tabItems.length > 0) {
      const id = this.tabItems[0].id;
      this.fetchNewsList(id);
    }

    this.initStickyScroll();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    initStickyScroll() {
      this.$nextTick(() => {
        this.leftSideEl = this.$refs.leftSide;
        this.tabsContentEl = this.$refs.tabsContent;

        if (!this.leftSideEl || !this.tabsContentEl || getComputedStyle(this.leftSideEl).display === 'none') {
          return;
        }

        const leftSideRect = this.leftSideEl.getBoundingClientRect();
        const tabsContentRect = this.tabsContentEl.getBoundingClientRect();

        this.initialCssTop = 0; // The original CSS 'top' value
        this.stickyThresholdViewport = 100; // How many px from viewport top to stick

        this.docTopToTabsContent = tabsContentRect.top + window.pageYOffset;

        this.naturalTopOffsetInParent = (leftSideRect.top - tabsContentRect.top) - this.initialCssTop;

        const initialLeftSideDocTop = this.docTopToTabsContent + this.naturalTopOffsetInParent + this.initialCssTop;
        this.engageStickyScrollTop = initialLeftSideDocTop - this.stickyThresholdViewport;

        window.addEventListener('scroll', this.handleScroll);
      });
    },
    handleScroll() {
      if (!this.leftSideEl || !this.tabsContentEl || getComputedStyle(this.leftSideEl).display === 'none') {
        return;
      }

      const scrollTop = window.pageYOffset;

      if (scrollTop > this.engageStickyScrollTop) {
        let newTop = this.stickyThresholdViewport - this.docTopToTabsContent + scrollTop - this.naturalTopOffsetInParent;

        const leftSideHeight = this.leftSideEl.offsetHeight;
        const tabsContentHeight = this.tabsContentEl.offsetHeight;

        const bottomLimitCssTop = tabsContentHeight - this.naturalTopOffsetInParent - leftSideHeight;

        if (newTop > bottomLimitCssTop) {
          newTop = bottomLimitCssTop;
        }

        this.currentTopPx = newTop;
      } else {
        this.currentTopPx = this.initialCssTop;
      }
    },
    async fetchTabs() {
      const pid = getPageId('news') || DEFAULT_PID;

      const res = await this.$http.get('/common/category/getList', {
        pid,
        lang_code: getLang(),
      });

      if (res.code == 200) {
        this.tabItems = res.data.map((item) => ({
          ...item,
          title: item.category_name,
        }));
      }
    },
    async fetchNewsList(id) {
      this.loading = true;
      this.content = ''; // 可选：清空内容以触发过渡
      const res = await this.$http.get(
        '/api/news/pageList',
        { category_id: id, page: this.currentPage, per_page: this.pageSize },
        { headers: { lang: getLang() } }
      );

      if (res.code == 200 && res.data) {
        this.newsList = res.data.data;
        this.currentPage = res.data.current_page;
        this.total = res.data.total;
      }
      this.loading = false;
    },
    handleNavigate(item) {
      this.updateReadCount(item.id);

      if (item.type == NewsType.Link) {
        window.open(item.content);
      } else {
        this.$router.push({
          path: `/news/${item.id}`,
          params: { id: item.id },
        });
      }
    },
    handleIndexChange(index) {
      const id = this.tabItems[index].id;
      this.fetchNewsList(id);
    },
    handlePageChange(page) {
      this.currentPage = page;
      const id = this.tabItems[this.currentIndex].id;
      this.fetchNewsList(id);
    },
    updateReadCount(id) {
      this.$http.get('/api/news/pv', { id });
    },
    formatDate(dateStr) {
      return dayjs(dateStr).format('DD / MMM / YYYY').toLowerCase();
    },
  },
};
</script>
<style lang="less" scoped>
.news-container {
  width: 100%;

  .banner {
    display: block;
    width: 100%;
    height: auto;
    margin: 0 auto;
    object-fit: cover;
  }

  .tabs-container {
    margin-bottom: 20px;
    margin-top: 80px;
  }

  .tabs-content {
    margin-top: 20px;
    max-width: 80%;
    margin: 0 auto;

    /* Add clearfix */
    &::after {
      content: '';
      display: table;
      clear: both;
    }

    .left-side {
      float: left;
      vertical-align: top;
      width: 200px;
      padding-left: 20px;
      margin-right: 40px;
      position: relative;

      .album-title {
        font-size: 28px;
        margin-bottom: 20px;

        color: #ff5715;
        font-weight: normal;
      }

      .qrcode-title {
        margin-top: 36px;
        font-size: 24px;
      }
    }

    .right-side {
      float: right;
      overflow: hidden;
      vertical-align: top;
      width: auto;
      padding-left: 20px;
    }

    .news-list {
      .news-item {
        margin-bottom: 40px;
        max-width: 800px;

        &:last-child {
          margin-bottom: 0;
        }

        .news-item-cover-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 47.5%;
          overflow: hidden;
          margin-bottom: 16px;
          cursor: pointer;
        }

        .news-item-cover {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: block;
          max-width: 100%;
        }

        .news-item-title {
          font-size: 24px;
          font-weight: normal;
          color: #ff5715;
          margin: 14px 0;
        }

        .news-item-info {
          border-bottom: 1px solid #666;
          margin-top: 12px;
          margin-bottom: 8px;
          overflow: hidden;
          padding-bottom: 8px;

          .news-item-day {
            font-family: Arial-BoldMT;
            color: #666666;
            float: left;
          }

          .news-item-read {
            float: right;
            color: #666;
            font-size: 16px;
          }
        }

        .news-item-desc {
          font-size: 16px;
          color: #666;
          margin-bottom: 10px;
        }
      }
    }
  }
}

.tab-loading {
  min-height: 120px;
  color: #999;
  font-size: 18px;
  border-radius: 8px;
  transition: background 0.2s;
  text-align: center;
  line-height: 120px;
}

.news-empty {
  text-align: center;
  color: #aaa;
  font-size: 20px;
  margin: 60px 0;
}

/* 淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1280px) {
  .news-container {
    .tabs-content {
      width: auto;
      max-width: 80%;
      margin-left: auto;
      margin-right: auto;

      .left-side {
        display: none;
      }

      .right-side {
        float: none;
        width: auto;
        overflow: visible;
        padding-left: 0;
      }
    }
  }
}

@media (max-width: 900px) {
  .news-container {
    .banner {
      height: 400px;
      object-fit: cover;
    }

    .tabs-content {
      max-width: 90%;
      margin-left: auto;
      margin-right: auto;

      .news-item {
        .news-item-title {
          font-size: 20px;
        }
      }
    }

    .tabs-container {
      margin-bottom: 10px;
      margin-top: 20px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/news.vue